// ============================================================================
// TYPES & INTERFACES - WHATSAPP GROUPS
// ============================================================================

/**
 * Participante de um grupo
 */
export interface GroupParticipant {
  id: string;
  participantJid: string;
  participantName?: string;
  phoneNumber?: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  addedAt: string;
  addedBy?: string;
}

/**
 * Grupo do WhatsApp
 */
export interface WhatsAppGroup {
  id: string;
  groupJid: string;
  tenantId: string;
  instanceName: string;
  subject: string;
  description?: string;
  pictureUrl?: string;
  participantsCount: number;
  isAnnouncement: boolean;
  isLocked: boolean;
  isUserAdmin: boolean; // Se o usuário (instância) é admin do grupo
  ephemeralDuration: number;
  inviteCode?: string;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
  participants?: GroupParticipant[];
}

/**
 * Request para criar grupo
 */
export interface CreateGroupRequest {
  instanceName: string;
  subject: string;
  participants: string[];
  description?: string;
  isAnnouncement?: boolean;
  ephemeralDuration?: number;
}

/**
 * Request para atualizar grupo
 */
export interface UpdateGroupRequest {
  subject?: string;
  description?: string;
  pictureUrl?: string;
}

/**
 * Request para gerenciar participantes
 */
export interface UpdateParticipantsRequest {
  action: 'add' | 'remove' | 'promote' | 'demote';
  participants: string[];
}

/**
 * Request para atualizar configurações
 */
export interface UpdateSettingsRequest {
  isAnnouncement?: boolean;
  isLocked?: boolean;
  ephemeralDuration?: number;
}

/**
 * Request para enviar convites
 */
export interface SendInvitesRequest {
  numbers: string[];
  description?: string;
}

/**
 * Response padrão da API
 */
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

/**
 * Response de listagem de grupos
 */
export interface ListGroupsResponse {
  success: boolean;
  total: number;
  groups: WhatsAppGroup[];
}

/**
 * Response de detalhes do grupo
 */
export interface GroupDetailsResponse {
  success: boolean;
  group: WhatsAppGroup;
}

/**
 * Response de criação de grupo
 */
export interface CreateGroupResponse {
  success: boolean;
  message: string;
  group: WhatsAppGroup;
  evolutionResponse: any;
}

/**
 * Response de código de convite
 */
export interface InviteCodeResponse {
  success: boolean;
  inviteCode: string;
  inviteUrl: string;
}

/**
 * Response de revogação de convite
 */
export interface RevokeInviteResponse {
  success: boolean;
  message: string;
  newInviteCode: string;
  newInviteUrl: string;
}

/**
 * Filtros para listagem de grupos
 */
export interface GroupFilters {
  instanceName?: string;
  search?: string;
  sortBy?: 'name' | 'date' | 'participants';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Estado de loading
 */
export interface LoadingState {
  isLoading: boolean;
  operation?: 'list' | 'create' | 'update' | 'delete' | 'sync' | 'participants' | 'invite';
}

/**
 * Opções de mensagens efêmeras
 */
export const EPHEMERAL_OPTIONS = [
  { value: 0, label: 'Desativado' },
  { value: 86400, label: '24 horas' },
  { value: 604800, label: '7 dias' },
  { value: 7776000, label: '90 dias' }
] as const;

/**
 * Tipo de ação em participantes
 */
export type ParticipantAction = 'add' | 'remove' | 'promote' | 'demote';

/**
 * Estatísticas do grupo
 */
export interface GroupStats {
  totalGroups: number;
  totalParticipants: number;
  totalAdmins: number;
  announcementGroups: number;
  ephemeralGroups: number;
}

/**
 * Contato do sistema (para seleção de participantes)
 */
export interface Contact {
  id: string;
  nome: string;
  telefone: string;
  email?: string;
  tags?: string[];
}

/**
 * Instância WhatsApp (para seleção)
 */
export interface WhatsAppInstance {
  name: string;
  displayName?: string;
  status: string;
  provider: string;
}

/**
 * Estado do formulário de criação
 */
export interface CreateGroupFormData {
  instanceName: string;
  subject: string;
  description: string;
  participants: string[];
  isAnnouncement: boolean;
  isLocked: boolean;
  ephemeralDuration: number;
  pictureUrl?: string;
}

/**
 * Validação de formulário
 */
export interface FormErrors {
  instanceName?: string;
  subject?: string;
  participants?: string;
  description?: string;
}

/**
 * Passo do wizard de criação
 */
export type WizardStep = 1 | 2 | 3;

/**
 * Modal state
 */
export interface ModalState {
  isOpen: boolean;
  type?: 'create' | 'details' | 'participants' | 'settings' | 'invite';
  groupId?: string;
}

